import { useCallback, useEffect, useMemo, useState } from "react";

const getStableRowId = (row, idx) => {
  const ids =
    (Array.isArray(row?.lineElements) && row.lineElements) ||
    (Array.isArray(row?.original?.lineElements) && row.original.lineElements) ||
    [];
  if (ids.length) return `le:${ids.join("|")}`;
  return String(idx);
};

export const useRowSelection = (
  rowsData = [],
  onRowSelectionChange,
  options = {}
) => {
  const { initialRowSelection = null, disableLocalStorage = false, modelId = null } = options;
  const [rowSelection, setRowSelection] = useState(() => initialRowSelection || {});

  // External window: always take selection from provided initial state
  useEffect(() => {
    if (!disableLocalStorage) return;
    setRowSelection(initialRowSelection || {});
  }, [disableLocalStorage, initialRowSelection]);

  // Host window: legacy localStorage-based selection restore
  useEffect(() => {
    if (disableLocalStorage) return;
    try {
      const { getCurrentNamespace } = require('utils/crossTabSync');
      const currentNs = getCurrentNamespace() || modelId;
      const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
      const raw = localStorage.getItem(rowSelectionKey);
      if (raw) setRowSelection(JSON.parse(raw));
    } catch {}
  }, [disableLocalStorage, modelId])

  // Keep host selection in sync with external window via storage events
  useEffect(() => {
    if (disableLocalStorage) return;
    try {
      const { getCurrentNamespace } = require('utils/crossTabSync');
      const currentNs = getCurrentNamespace() || modelId;
      const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
      const onStorage = (e) => {
        if (e.storageArea !== localStorage) return;
        if (e.key === rowSelectionKey || e.key === "newTabOpened") {
          try {
            const raw = localStorage.getItem(rowSelectionKey);
            const parsed = raw ? JSON.parse(raw) : {};
            setRowSelection(parsed);
          } catch (err) {
            setRowSelection({});
          }
        }
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    } catch {}
  }, [disableLocalStorage, modelId]);

  const rowsById = useMemo(() => {
    const map = {};
    rowsData.forEach((row, idx) => {
      map[getStableRowId(row, idx)] = row;
    });
    return map;
  }, [rowsData]);

  const selectedRows = useMemo(() => {
    const rowsSelectionData = Object.entries(rowSelection);

    return rowsSelectionData.length
      ? rowsSelectionData
        .filter(([_, value]) => !!value)
        .map(([key]) => {
          // When react-table uses stable ids (e.g. le:...), map by id
          if (rowsById[key]) return rowsById[key];
          // Backward-compat: numeric keys used to be array indices
          const idx = Number(key);
          return Number.isFinite(idx) ? rowsData[idx] : undefined;
        })
        .filter(Boolean)
      : [];
  }, [rowsData, rowSelection, rowsById]);

  const handleClearSelection = useCallback(() => setRowSelection({}), []);

  useEffect(() => {
    onRowSelectionChange?.(selectedRows);
  }, [onRowSelectionChange, selectedRows]);

  return {
    rowSelection,
    handleRowSelectionChange: setRowSelection,
    handleClearSelection,
  };
};

export default useRowSelection;
